import { Button } from '@aws-amplify/ui-react';
import type { Edulead } from 'edulead';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TbPlus, TbSearch } from 'react-icons/tb';
import { GlobalContext } from '../../global-context';
import { allPages, toastV4Error } from '../../services/edulead-v4';
import { eduleadV4 } from '../../services/edulead-v4-client';
import { useNavigatorStore } from '../../zustand/useNavigatorStore';
import { NavigatorQuery, parseNavigatorQuery, serializeNavigatorQuery } from './navigator-query';
import {
  isRequirementMet,
  NAVIGATOR_FILTERS,
  NavigatorFilter,
  NavigatorFilterState,
  NavigatorSectionId,
} from './navigator-filters';
import { NavigatorFilterRow } from './NavigatorFilterRow';
import './NavigatorPanel.scss';

/** The parts of the query, in the order the panel shows them. */
const SECTIONS: { id: NavigatorSectionId; title: string }[] = [
  { id: 'location', title: 'Location' },
  { id: 'expansion', title: 'Expansion' },
  { id: 'institutions', title: 'Institutions' },
  { id: 'people', title: 'People' },
];

/** A row of the panel: a filter with its operator and value. A filter may be on several rows. */
interface PanelRow {
  id: number;
  filter: NavigatorFilter;
  state: NavigatorFilterState;
}

let nextRowId = 1;
const newRow = (
  filter: NavigatorFilter,
  state: NavigatorFilterState = { operator: filter.defaultOperator, value: '' },
): PanelRow => ({ id: nextRowId++, filter, state });

/**
 * The rows for a query from the URL: its rules, each a row; with no query, an empty row per
 * starter filter to start from.
 */
function rowsOf(query: NavigatorQuery, filters: NavigatorFilter[]): PanelRow[] {
  if (query.length === 0) return filters.filter((f) => f.starter).map((filter) => newRow(filter));
  return query.flatMap((rule) => {
    const filter = filters.find((f) => f.section === rule.section && f.field === rule.field);
    return filter
      ? [newRow(filter, { operator: rule.operator, value: rule.value, disabled: rule.disabled })]
      : [];
  });
}

/**
 * One section of the query: its rows, each a filter, and a plus that adds one. The section's
 * starter filters are rows to begin with; a row turns into any filter of the section by its
 * label, its trash takes it off the panel, and the plus adds a row of the first filter not on
 * the panel yet (the section's first once all are) to turn into any, also one already there
 * (two rules on one field, say State is X and State is not Y).
 */
function QuerySection({
  title,
  filters,
  rows,
  allRows,
  beacons,
  territories,
  onChange,
  onSwap,
  onRemove,
  onAdd,
}: {
  title: string;
  filters: NavigatorFilter[];
  rows: PanelRow[];
  /** Every section's rows, for the requirements on other sections' filters. */
  allRows: PanelRow[];
  beacons: Edulead.EduleadV4Beacon[];
  territories: Edulead.EduleadV4Territory[];
  onChange: (id: number, state: NavigatorFilterState) => void;
  onSwap: (id: number, next: NavigatorFilter) => void;
  onRemove: (id: number) => void;
  onAdd: (filter: NavigatorFilter) => void;
}) {
  const unused = filters.find((f) => !rows.some((row) => row.filter === f)) ?? filters[0];
  const rules = allRows.map((row) => ({
    section: row.filter.section,
    field: row.filter.field,
    ...row.state,
  }));
  return (
    <div className="navigator-panel__section">
      <div className="navigator-panel__section-header">
        <span className="navigator-panel__section-title">{title}</span>
        <Button
          size="small"
          className="nohover navigator-panel__add-button"
          aria-label="Add filter"
          onClick={() => onAdd(unused)}
        >
          <TbPlus />
        </Button>
      </div>
      {rows.map((row) => (
        <NavigatorFilterRow
          key={row.id}
          filter={row.filter}
          state={row.state}
          beacons={beacons}
          territories={territories}
          alternatives={filters}
          isRequirementMet={isRequirementMet(row.filter, rules)}
          onChange={(state) => onChange(row.id, state)}
          onSwap={(next) => onSwap(row.id, next)}
          onRemove={() => onRemove(row.id)}
        />
      ))}
    </div>
  );
}

/**
 * The navigator's right panel: the query the views show (its location, institution and contact
 * filters). The query is edited here and goes into the URL's ?q= on
 * Search, as the directories' does; the views read it from there. It replaced the lens panel
 * (lenses were retired 2026-11-02).
 */
export function NavigatorPanel() {
  const globalContext = useContext(GlobalContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [beacons, setBeacons] = useState<Edulead.EduleadV4Beacon[]>([]);
  const [territories, setTerritories] = useState<Edulead.EduleadV4Territory[]>([]);
  // The map's count of the query's people, while the Map view has one.
  const headcountTotals = useNavigatorStore((state) => state.headcountTotals);
  const isCounting = useNavigatorStore((state) => state.isCountingHeadcounts);
  const isLoadingList = useNavigatorStore((state) => state.isLoadingList);
  const isSearching = isCounting || isLoadingList;

  const teamId = globalContext?.authInfo?.teamId ?? '';
  // A filter whose requirement is not on the panel is not either (none today: beacons went GA
  // 2026-11-03, so the radius always has its beacons).
  const filters = useMemo(
    () =>
      NAVIGATOR_FILTERS.filter(
        (f) =>
          !f.requires ||
          NAVIGATOR_FILTERS.some(
            (r) => r.section === f.requires?.section && r.field === f.requires.field,
          ),
      ),
    [],
  );
  // A section with no filter left is not shown.
  const sections = SECTIONS.filter((section) => filters.some((f) => f.section === section.id));

  /**
   * The rows being edited. A new URL (a saved search opened, back) replaces them, except the
   * one the panel's own Search put there: the rows stay as they were, empty ones included.
   */
  const [rows, setRows] = useState<PanelRow[]>(() =>
    rowsOf(parseNavigatorQuery(location.search), filters),
  );
  const searched = useRef<string | null>(null);
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('q');
    if (q !== null && q === searched.current) return;
    setRows(rowsOf(parseNavigatorQuery(location.search), filters));
  }, [location.search, filters]);

  useEffect(() => {
    if (!teamId) return;
    const load = async () => {
      try {
        // A team holds at most its NR_BEACONS__TOTAL (200 unless raised): two pages at most.
        setBeacons(await allPages(eduleadV4.beacons.list({ page_size: 100, order_by: 'name' })));
      } catch (error) {
        toastV4Error(error, 'Failed to load beacons');
      }
    };
    load();
  }, [teamId]);

  useEffect(() => {
    if (!teamId) return;
    const load = async () => {
      try {
        // A team holds at most 50 territories: one page.
        const all = await allPages(eduleadV4.territories.list({ page_size: 50 }));
        setTerritories(all.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')));
      } catch (error) {
        toastV4Error(error, 'Failed to load territories');
      }
    };
    load();
  }, [teamId]);

  const changeRow = (id: number, state: NavigatorFilterState) =>
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, state } : row)));
  const swapRow = (id: number, next: NavigatorFilter) =>
    setRows((prev) =>
      prev.map((row) => (row.id === id && row.filter !== next ? newRow(next) : row)),
    );
  const removeRow = (id: number) => setRows((prev) => prev.filter((row) => row.id !== id));
  const addRow = (filter: NavigatorFilter) => setRows((prev) => [...prev, newRow(filter)]);

  // Search puts the query in the URL (the set rows, in their order); a list starts again from
  // its first page.
  const search = () => {
    const query: NavigatorQuery = rows.map((row) => ({
      section: row.filter.section,
      field: row.filter.field,
      ...row.state,
    }));
    const params = new URLSearchParams(location.search);
    const q = serializeNavigatorQuery(query);
    searched.current = q;
    params.set('q', q);
    params.delete('page');
    params.delete('pageToken');
    navigate(`${location.pathname}?${params.toString()}`);
  };

  return (
    <div className="navigator-panel">
      <div className="navigator-panel__body">
        {sections.map((section) => (
          <QuerySection
            key={section.id}
            title={section.title}
            filters={filters.filter((f) => f.section === section.id)}
            rows={rows.filter((row) => row.filter.section === section.id)}
            allRows={rows}
            beacons={beacons}
            territories={territories}
            onChange={changeRow}
            onSwap={swapRow}
            onRemove={removeRow}
            onAdd={addRow}
          />
        ))}
      </div>

      <div className="navigator-panel__actions">
        <div className="navigator-panel__status">
          {/* Nothing while counting: the Search button's spinner says so, and the last totals
              are not this query's. */}
          {!isCounting && headcountTotals && (
            <span className="navigator-panel__totals">
              {headcountTotals.people.toLocaleString()}{' '}
              {headcountTotals.people === 1 ? 'person' : 'people'} at{' '}
              {headcountTotals.accounts.toLocaleString()}{' '}
              {headcountTotals.accounts === 1 ? 'account' : 'accounts'}
            </span>
          )}
        </div>
        <Button
          size="small"
          className="white"
          isFullWidth
          gap={6}
          isLoading={isSearching}
          loadingText="Searching"
          onClick={search}
        >
          <TbSearch />
          Search
        </Button>
      </div>
    </div>
  );
}
